import * as React from "react";

import { ClientViewingContext } from "@/shared/domain/agentThread/AgentThreadRecord";

import { ContextType } from "./contextType";
import {
  ContextDisplayNames,
  useClientViewingContext,
} from "./hooks/useClientViewingContext";

const THREAD_ID_STORAGE_KEY = "roboto-ai-agent-thread-current-thread-id";

/**
 * AIAgentThreadContext provides UI state only - no data fetching or mutations.
 * Data fetching and mutations are handled by the AgentThread component.
 */
interface AIAgentThreadContextValue {
  // UI state
  isFullscreen: boolean;
  showHistory: boolean;
  autoEnabled: boolean;
  currentThreadId: string | undefined;
  context: ClientViewingContext;
  contextDisplayNames: ContextDisplayNames;
  modelProfile: string | undefined;

  // UI state setters
  setIsFullscreen: (value: boolean) => void;
  setShowHistory: (value: boolean) => void;
  setAutoEnabled: (value: boolean) => void;
  setCurrentThreadId: (threadId: string | undefined) => void;
  setModelProfile: (profile: string) => void;

  // Convenience handlers
  handleContextDelete: (
    contextType: ContextType,
    contextContent: string,
  ) => void;
  handleNewThread: () => void;
  handleToggleFullscreen: () => void;
  handleToggleHistory: () => void;
  handleToggleAuto: () => void;
  handleHistoryThreadClicked: (threadId: string) => void;
  handleLinkClick: () => void;
  handleResetUIState: () => void;

  /** Optional admin-only per-message slot: returns a debug control to render
   *  under a message, or nothing. Undefined outside the admin thread explorer.
   *  Lives on the context (not a prop) so it reaches AgentThreadByIdView
   *  whether the thread is rendered interactively or read-only. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;
}

// eslint-disable-next-line react-refresh/only-export-components
export const AIAgentThreadContext = React.createContext<
  AIAgentThreadContextValue | undefined
>(undefined);

interface AIAgentThreadProviderProps {
  children: React.ReactNode;
  /** Optional initial thread id. When set, the provider starts on that thread
   *  and skips the sessionStorage read AND the sessionStorage write — the
   *  caller (e.g. the admin thread explorer) owns its own thread selection and
   *  must not clobber the regular sidebar's last-active thread. The lazy
   *  initializer reads ``initialThreadId`` once at mount; remount with a new
   *  ``key`` if the caller needs to swap threads from outside. */
  initialThreadId?: string;
  /** Fires whenever the active thread id changes. Lets parent wrappers keep
   *  their own state (e.g. a selected-thread id) in sync with forks and
   *  history-pane clicks. The handler is invoked via a ref so passing an
   *  inline arrow doesn't restart the effect on every render. */
  onThreadIdChanged?: (threadId: string | undefined) => void;
  /** Forwarded into context so message rows can render an admin-only debug
   *  control. Only the admin thread explorer sets this. */
  renderMessageDebugControl?: (messageSequenceNum: number) => React.ReactNode;
}

export const AIAgentThreadProvider: React.FC<AIAgentThreadProviderProps> = ({
  children,
  initialThreadId,
  onThreadIdChanged,
  renderMessageDebugControl,
}) => {
  // ``initialThreadId`` is the signal that this provider is being used outside
  // the regular sidebar (today: only ``AdminAgentThreadViewer``). When set, we skip
  // both the sessionStorage read and the sessionStorage write so an admin
  // poking around in the explorer never overwrites the user's last-active
  // sidebar thread.
  const ownsSessionStorage = initialThreadId === undefined;
  // UI state
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [showHistory, setShowHistory] = React.useState(false);
  const [autoEnabled, setAutoEnabled] = React.useState(() => {
    const stored = sessionStorage.getItem("autoNavEnabled");
    return stored === "true";
  });

  // Model profile — undefined means "use the default from API"
  const [modelProfile, setModelProfile] = React.useState<string | undefined>(
    undefined,
  );

  const { context, displayNames, handleContextDelete } =
    useClientViewingContext();

  // Thread ID state with sessionStorage persistence (sidebar use only).
  const [currentThreadId, setCurrentThreadId] = React.useState<
    string | undefined
  >(() => {
    if (initialThreadId) {
      return initialThreadId;
    }
    if (!ownsSessionStorage) {
      return undefined;
    }
    try {
      return sessionStorage.getItem(THREAD_ID_STORAGE_KEY) || undefined;
    } catch {
      return undefined;
    }
  });

  // Persist thread ID to sessionStorage whenever it changes — but only when
  // this provider actually owns the storage slot.
  React.useEffect(() => {
    if (!ownsSessionStorage) {
      return;
    }
    if (currentThreadId) {
      try {
        sessionStorage.setItem(THREAD_ID_STORAGE_KEY, currentThreadId);
      } catch {
        // Ignore sessionStorage errors
      }
    } else {
      try {
        sessionStorage.removeItem(THREAD_ID_STORAGE_KEY);
      } catch {
        // Ignore sessionStorage errors
      }
    }
  }, [currentThreadId, ownsSessionStorage]);

  // Route ``onThreadIdChanged`` through a ref so an inline arrow at the call
  // site doesn't restart the effect every render. The effect fires only
  // when the thread id actually changes, and always calls the latest handler.
  const onAgentThreadIdChangedRef = React.useRef(onThreadIdChanged);
  React.useEffect(() => {
    onAgentThreadIdChangedRef.current = onThreadIdChanged;
  }, [onThreadIdChanged]);
  React.useEffect(() => {
    onAgentThreadIdChangedRef.current?.(currentThreadId);
  }, [currentThreadId]);

  // UI State Handlers
  const handleNewThread = () => {
    setCurrentThreadId(undefined);
    setShowHistory(false);
    setModelProfile(undefined);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => !prev);
  };

  const handleToggleHistory = () => {
    setShowHistory((prev) => !prev);
  };

  const handleToggleAuto = () => {
    setAutoEnabled((prev) => {
      const newValue = !prev;
      sessionStorage.setItem("autoNavEnabled", String(newValue));
      return newValue;
    });
  };

  const handleHistoryThreadClicked = (threadId: string) => {
    setCurrentThreadId(threadId);
    setShowHistory(false);
  };

  const handleLinkClick = () => {
    setIsFullscreen(false);
  };

  const handleResetUIState = () => {
    setShowHistory(false);
    setIsFullscreen(false);
  };

  const value: AIAgentThreadContextValue = {
    // UI state
    isFullscreen,
    showHistory,
    autoEnabled,
    currentThreadId,
    context,
    contextDisplayNames: displayNames,
    modelProfile,

    // UI state setters
    setIsFullscreen,
    setShowHistory,
    setAutoEnabled,
    setCurrentThreadId,
    setModelProfile,

    // Convenience handlers
    handleContextDelete,
    handleNewThread,
    handleToggleFullscreen,
    handleToggleHistory,
    handleToggleAuto,
    handleHistoryThreadClicked,
    handleLinkClick,
    handleResetUIState,

    renderMessageDebugControl,
  };

  return (
    <AIAgentThreadContext.Provider value={value}>
      {children}
    </AIAgentThreadContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAIAgentThread = (): AIAgentThreadContextValue => {
  const context = React.useContext(AIAgentThreadContext);
  if (context === undefined) {
    throw new Error(
      "useAIAgentThread must be used within an AIAgentThreadProvider",
    );
  }
  return context;
};
